import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';

interface HubSpotContact {
  id: string;
  properties: {
    firstname?: string;
    lastname?: string;
    email?: string;
    company?: string;
  };
}

export const HubSpotOAuthIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [contacts, setContacts] = useState<HubSpotContact[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('crm_integrations')
        .select('*')
        .eq('user_id', user.id)
        .eq('crm_type', 'hubspot')
        .eq('status', 'active')
        .single();

      setIsConnected(!!data);
    } catch (error) {
      console.error('Error checking HubSpot connection:', error);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the OAuth initialization edge function
      const { data, error } = await supabase.functions.invoke('crm-oauth-initialize', {
        body: { crmType: 'hubspot' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to initialize OAuth');
      }

      if (data?.authUrl) {
        // Redirect to OAuth authorization URL
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error: any) {
      console.error('HubSpot OAuth error:', error);
      toast.error(error.message || 'Failed to start OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadContacts = async () => {
    if (!isConnected) return;

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: integration } = await supabase
        .from('crm_integrations')
        .select('oauth_data')
        .eq('user_id', user.id)
        .eq('crm_type', 'hubspot')
        .single();

      if (!integration?.oauth_data || typeof integration.oauth_data !== 'object' || !('access_token' in integration.oauth_data)) {
        toast.error('No access token found. Please reconnect to HubSpot.');
        return;
      }

      const oauthData = integration.oauth_data as { access_token: string };
      const response = await fetch('https://api.hubapi.com/crm/v3/objects/contacts?limit=5&properties=firstname,lastname,email,company', {
        headers: {
          'Authorization': `Bearer ${oauthData.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Access token expired. Please reconnect to HubSpot.');
          setIsConnected(false);
          return;
        }
        throw new Error('Failed to fetch contacts');
      }

      const data = await response.json();
      setContacts(data.results || []);
      toast.success(`Loaded ${data.results?.length || 0} contacts from HubSpot`);
    } catch (error: any) {
      console.error('Error loading contacts:', error);
      toast.error('Failed to load contacts from HubSpot');
    } finally {
      setIsLoading(false);
    }
  };

  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with HubSpot data...');
      // Trigger AI workflow that will process HubSpot data
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'hubspot',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('crm_integrations')
        .delete()
        .eq('user_id', user.id)
        .eq('crm_type', 'hubspot');

      setIsConnected(false);
      setContacts([]);
      toast.success('Disconnected from HubSpot');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from HubSpot');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-orange-500 rounded-sm flex items-center justify-center">
              <span className="text-white text-xs font-bold">H</span>
            </div>
            HubSpot CRM
            {isConnected && (
              <Badge variant="default" className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect your HubSpot account using secure OAuth to sync contacts, deals, and companies automatically.
            </AlertDescription>
          </Alert>

          {!isConnected ? (
            <Button 
              onClick={handleOAuthConnect} 
              disabled={isConnecting}
              className="w-full"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting to HubSpot...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect with HubSpot OAuth
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Button onClick={loadContacts} disabled={isLoading} variant="outline">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load Contacts'
                  )}
                </Button>
                <Button onClick={runAIWorkflow} variant="outline">
                  Run AI Workflow
                </Button>
                <Button onClick={disconnect} variant="outline">
                  Disconnect
                </Button>
              </div>

              {contacts.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Recent Contacts</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {contacts.slice(0, 3).map((contact) => (
                      <div key={contact.id} className="p-3 border rounded-lg">
                        <div className="font-medium">
                          {contact.properties.firstname} {contact.properties.lastname}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {contact.properties.email}
                        </div>
                        {contact.properties.company && (
                          <div className="text-sm text-muted-foreground">
                            {contact.properties.company}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};